import React, { memo, useCallback } from 'react'
import tw from 'twin.macro'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { CustomerDetailsCard } from 'src/components/customer/shared/CustomerDetailsCard'
import { SectionCardSkeleton } from 'src/components/shared/SectionCardSkeleton'
import { AdminFetchCustomer } from 'src/constants/actionTypes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { getCustomer } from 'src/selectors/customer'

/**
 * The customer's landing tab: the details card (identity, status, "log in as"). The card lives
 * here rather than in `CustomerLayout` so the other tabs keep their full height for their own
 * content; navigation into the setup areas is the side menu's job.
 */
const CustomerOverviewPageComponent = () => {
  const { customerId } = useParams<{ customerId: string }>()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const customer = useSelector(getCustomer)

  const refetchCustomer = useCallback(() => {
    if (!customerId) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchCustomer.request({ customerId }))
  }, [customerId, asyncDispatchWithErrorToast])

  if (!customerId) {
    return null
  }

  // `CustomerLayout` fetches the customer on select; the card waits for THIS customer's record
  // so a stale one from the previous selection never shows under the new id.
  const isCustomerReady = Boolean(customer && customer.id === customerId)

  return (
    <div
      data-testid="admin-customer-overview-page"
      css={tw`flex w-full flex-row justify-center p-6`}
    >
      {isCustomerReady && customer ? (
        <CustomerDetailsCard customer={customer} onCustomerChanged={refetchCustomer} />
      ) : (
        <div css={tw`w-full max-w-[520px]`}>
          <SectionCardSkeleton fields={2} dataTestId="admin-customer-overview-loading" />
        </div>
      )}
    </div>
  )
}

export const CustomerOverviewPage = memo(CustomerOverviewPageComponent)
